import { useState } from "react";

interface Props {
  onClose: () => void;
}

const categories = ["Electronics", "Fashion & Apparel", "Home & Furniture", "Beauty & Skincare", "Sports & Fitness", "Food & Beverages", "Packaging", "Precision Tools", "Luxury Goods", "Other"];

export default function FactoryProductUploadModal({ onClose }: Props) {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    factoryName: "",
    country: "",
    city: "",
    category: "",
    certifications: "",
    productTitle: "",
    productDesc: "",
    unitPrice: "",
    moqUnits: "",
    moqUSD: "",
    samplePrice: "",
    sampleLeadTime: "",
    bulkLeadTime: "",
    containerTypes: [] as string[],
    customBranding: false,
    contactEmail: "",
    contactPhone: "",
    website: "",
  });

  const toggleContainer = (ct: string) => {
    setForm((prev) => ({
      ...prev,
      containerTypes: prev.containerTypes.includes(ct)
        ? prev.containerTypes.filter((c) => c !== ct)
        : [...prev.containerTypes, ct],
    }));
  };

  const handleSubmit = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1500);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl w-full max-w-md p-10 text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5" style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}>
            <i className="ri-building-2-line text-white text-4xl" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Factory Listing Submitted!</h2>
          <p className="text-gray-500 text-sm mb-4">
            Your factory <strong>{form.factoryName}</strong> and product <strong>{form.productTitle}</strong> have been submitted for verification.
          </p>
          <div className="rounded-xl p-4 mb-5 text-left space-y-2" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Unit Price</span><span className="font-bold text-gray-900">${form.unitPrice}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">MOQ</span><span className="font-bold text-gray-900">{form.moqUnits} units / ${form.moqUSD}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Sample Price</span><span className="font-bold text-green-600">${form.samplePrice}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Containers</span><span className="font-bold text-gray-900">{form.containerTypes.join(", ") || "TBD"}</span></div>
          </div>
          <p className="text-xs text-gray-400 mb-6">Our team will verify your factory within 2-3 business days. You will receive a confirmation at <strong>{form.contactEmail}</strong>.</p>
          <button onClick={onClose} className="px-8 py-3 text-white font-bold rounded-full cursor-pointer whitespace-nowrap" style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}>
              <i className="ri-building-2-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-gray-900">List Your Factory</h2>
              <p className="text-xs text-gray-400">Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer text-gray-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-0 px-6 pt-4 pb-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 transition-all ${step >= s ? "text-white" : "bg-gray-100 text-gray-400"}`}
                style={step >= s ? { background: "linear-gradient(135deg, #B8860B, #FFD700)" } : {}}>
                {step > s ? <i className="ri-check-line" /> : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 mx-1 rounded-full transition-all ${step > s ? "" : "bg-gray-100"}`} style={step > s ? { background: "linear-gradient(90deg, #B8860B, #FFD700)" } : {}} />}
            </div>
          ))}
        </div>
        <div className="flex justify-between px-6 pb-4 text-xs text-gray-400">
          <span>Factory Info</span>
          <span>Product & Pricing</span>
          <span>Contact</span>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Step 1: Factory Info */}
          {step === 1 && (
            <>
              <div className="p-4 rounded-xl border border-amber-200" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <i className="ri-information-line" /> Factory Information
                </p>
                <p className="text-xs text-amber-700 mt-1">Tell buyers about your factory. All listings are verified before going live.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Factory / Company Name *</label>
                  <input value={form.factoryName} onChange={(e) => setForm({ ...form, factoryName: e.target.value })} placeholder="e.g. SinoTech Electronics Co." className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Country *</label>
                  <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. China, USA, Germany" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">City *</label>
                  <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Shenzhen, Los Angeles" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Product Category *</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer">
                    <option value="">Select category...</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Certifications</label>
                  <input value={form.certifications} onChange={(e) => setForm({ ...form, certifications: e.target.value })} placeholder="e.g. ISO 9001, CE, FDA, GOTS (comma separated)" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!form.factoryName || !form.country || !form.category}
                className="w-full py-3 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 transition-all"
                style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}
              >
                Next: Product & Pricing <i className="ri-arrow-right-line ml-1" />
              </button>
            </>
          )}

          {/* Step 2: Product & Pricing */}
          {step === 2 && (
            <>
              <div className="p-4 rounded-xl border border-amber-200" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <i className="ri-price-tag-3-line" /> Product & Bulk Pricing
                </p>
                <p className="text-xs text-amber-700 mt-1">Set your product details, unit price, MOQ, and container options.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Product Title *</label>
                <input value={form.productTitle} onChange={(e) => setForm({ ...form, productTitle: e.target.value })} placeholder="e.g. TWS Wireless Earbuds OEM/ODM" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Product Description</label>
                <textarea value={form.productDesc} onChange={(e) => setForm({ ...form, productDesc: e.target.value })} rows={3} maxLength={500} placeholder="Describe your product, materials, specifications, customization options..." className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Unit Price (USD) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input type="number" value={form.unitPrice} onChange={(e) => setForm({ ...form, unitPrice: e.target.value })} placeholder="0.00" className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">MOQ (Units) *</label>
                  <input type="number" value={form.moqUnits} onChange={(e) => setForm({ ...form, moqUnits: e.target.value })} placeholder="e.g. 1000" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">MOQ (USD) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input type="number" value={form.moqUSD} onChange={(e) => setForm({ ...form, moqUSD: e.target.value })} placeholder="8000" className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sample Price (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input type="number" value={form.samplePrice} onChange={(e) => setForm({ ...form, samplePrice: e.target.value })} placeholder="e.g. 150" className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sample Lead Time</label>
                  <input value={form.sampleLeadTime} onChange={(e) => setForm({ ...form, sampleLeadTime: e.target.value })} placeholder="e.g. 5-7 days" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Bulk Lead Time</label>
                  <input value={form.bulkLeadTime} onChange={(e) => setForm({ ...form, bulkLeadTime: e.target.value })} placeholder="e.g. 25-35 days" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Container Types Available</label>
                <div className="flex flex-wrap gap-2">
                  {["20ft", "40ft", "40ft HC"].map((ct) => (
                    <button
                      key={ct}
                      type="button"
                      onClick={() => toggleContainer(ct)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition-all cursor-pointer ${form.containerTypes.includes(ct) ? "text-white border-transparent" : "border-gray-200 text-gray-600 hover:border-amber-300"}`}
                      style={form.containerTypes.includes(ct) ? { background: "linear-gradient(135deg, #B8860B, #FFD700)" } : {}}
                    >
                      <i className="ri-truck-line" />{ct}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-amber-50 rounded-xl border border-amber-100">
                <input type="checkbox" checked={form.customBranding} onChange={(e) => setForm({ ...form, customBranding: e.target.checked })} className="w-4 h-4 accent-amber-500" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Custom Branding / Private Label Available</p>
                  <p className="text-xs text-gray-500">Buyers can add their logo and brand name to your products</p>
                </div>
              </label>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-xl hover:border-gray-300 cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line mr-1" /> Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!form.productTitle || !form.unitPrice || !form.moqUnits}
                  className="flex-1 py-3 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}
                >
                  Next: Contact <i className="ri-arrow-right-line ml-1" />
                </button>
              </div>
            </>
          )}

          {/* Step 3: Contact */}
          {step === 3 && (
            <>
              <div className="p-4 rounded-xl border border-amber-200" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <i className="ri-contacts-line" /> Contact Information
                </p>
                <p className="text-xs text-amber-700 mt-1">Buyers will use this to reach you. Keep it accurate.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Business Email *</label>
                  <input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} placeholder="factory@company.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phone / WhatsApp</label>
                  <input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} placeholder="+1 (555) 000-0000" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Website (optional)</label>
                  <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://yourfactory.com" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>

              {/* Summary */}
              <div className="rounded-xl p-4 border border-amber-200" style={{ background: "linear-gradient(135deg, #FFF8E1, #FFFDE7)" }}>
                <p className="text-sm font-bold text-amber-900 mb-3">Listing Summary</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Factory</span><span className="font-semibold text-gray-900">{form.factoryName}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-semibold text-gray-900">{form.city}, {form.country}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Product</span><span className="font-semibold text-gray-900 text-right max-w-xs truncate">{form.productTitle}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Unit Price</span><span className="font-bold text-amber-700">${form.unitPrice}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">MOQ</span><span className="font-bold text-gray-900">{form.moqUnits} units / ${form.moqUSD}</span></div>
                  {form.samplePrice && <div className="flex justify-between"><span className="text-gray-500">Sample</span><span className="font-bold text-green-600">${form.samplePrice}</span></div>}
                  {form.containerTypes.length > 0 && <div className="flex justify-between"><span className="text-gray-500">Containers</span><span className="font-bold text-gray-900">{form.containerTypes.join(", ")}</span></div>}
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-xl hover:border-gray-300 cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line mr-1" /> Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !form.contactEmail}
                  className="flex-1 py-3 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)" }}
                >
                  {loading ? <span><i className="ri-loader-4-line animate-spin mr-2" />Submitting...</span> : <span><i className="ri-building-2-line mr-1" />Submit Listing</span>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
